import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { ListViewGridContentGenerator, ListViewGridContentGeneratorBaseParams, } from './ListViewGridContentGenerator';
import { doesOrderClauseSupportDirections, getPrimaryOrderClause } from './SortableHeader';

export interface ListViewStateProps {
  readonly displaySchedulesInClientTimezone: boolean;
  readonly ignoreExtractTaskPriority: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
}

export interface ListViewDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
}

export type ListViewGridContentGeneratorBaseProps
  = Omit<ListViewGridContentGeneratorBaseParams, keyof ListViewStateProps>;

export interface ListViewOwnProps extends ListViewGridContentGeneratorBaseProps {
  readonly isBusyFetchingItems: boolean;
}

export type ListViewProps = ListViewOwnProps & ListViewStateProps & ListViewDispatchProps;

export class ListView extends React.Component<ListViewProps> {

  private contentGenerator: ListViewGridContentGenerator = null;

  public render(): JSX.Element {
    if (this.props.isBusyFetchingItems || this.props.items.length === 0) {
      // If we have no items don't render anything
      // If the initial render (which doesn't call shouldComponentUpdate) is still busy fetching items
      // Don't render anything.
      return null;
    }

    if (!this.contentGenerator) {
      this.contentGenerator = new ListViewGridContentGenerator(this.props);
    } else {
      this.contentGenerator.generateContent(this.props);
    }

    const primaryOrderClause = getPrimaryOrderClause(this.props.currentSort);
    const sortAscending = doesOrderClauseSupportDirections(primaryOrderClause) ? primaryOrderClause.ascending : undefined;

    const props: DataGrid.Props = {
      cols: this.contentGenerator.cols,
      rowStore: this.contentGenerator.rowStore(),
      config: {
        colsPerChunk: this.contentGenerator.cols.length,
        rememberLastScrollPos: true,
        // For empty list view, make headers wide enough so text fits
        ...(this.props.items.length === 0 && {minimumColumnWidth: 65}),
        rowOuterHeight: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT
      },
      selectionModel: this.contentGenerator.selection(),
      showVerticalGridLines: false,
      showHorizontalGridLines: true,
      showRowBanding: false,
      sortKey: primaryOrderClause.field,
      sortAscending: sortAscending,
    };

    const classNames = ['tb-spaces-list-view', ExplorerStyles.ListView];
    if (props.cols.length > 0) {
      classNames.push(ExplorerStyles.DataGridBodyTopBorder);
    }

    return (
      <div className={classNames.join(' ')} data-tb-test-id="tb-list-view">
        <DataGrid {...props} />
      </div>
    );
  }

  // When filtering on content type, the content type property will cause a rerender
  // however we won't have the updated items yet which can cause a crash.
  // because we could filter down to content type specific columns but we haven't yet finished
  // fetching those specific items from server yet.
  // So only rerender if we are told we aren't busy fetching new items.
  // Because Explorer (parent) is a pure component, we won't re render if items haven't changed
  public shouldComponentUpdate(nextProps: ListViewProps): boolean {
    return !nextProps.isBusyFetchingItems;
  }
}
